import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const SpecSchemaContext = createContext(null);

/** Category-specific specification fields, loaded once from the backend. */
export function SpecSchemaProvider({ children }) {
  const [categories, setCategories] = useState(null);

  const load = useCallback(() => api.get("/spec-schema")
    .then(({ data }) => setCategories(data.categories || []))
    .catch(() => setCategories([])), []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => {
    const list = categories || [];
    const byCategory = Object.fromEntries(list.map((c) => [c.category, c]));
    const fallback = byCategory["Cable Glands"] || list[0];

    const forCategory = (category) => byCategory[category] || fallback || { fields: [], mandatory: [] };

    /** Editable inputs for a category: a range field becomes two numeric inputs. */
    const inputsFor = (category) => forCategory(category).fields.flatMap((f) => (
      f.type === "range"
        ? [[f.min_key, `${f.label} min${f.unit ? ` (${f.unit})` : ""}`, "number", []],
           [f.max_key, `${f.label} max${f.unit ? ` (${f.unit})` : ""}`, "number", []]]
        : [[f.key, f.unit ? `${f.label} (${f.unit})` : f.label,
            f.type === "number" ? "number" : "text", f.options || []]]
    ));

    /** Display parameters (range shown as one row, matching the comparison table). */
    const displayFor = (category) => forCategory(category).fields.map((f) => [f.key, f.label, f]);

    const value_ = (spec, field) => {
      if (!spec) return "Not stated";
      if (field?.type === "range") {
        const a = spec[field.min_key];
        const b = spec[field.max_key];
        return a != null && b != null ? `${a} to ${b} ${field.unit || ""}`.trim() : "Not stated";
      }
      const v = spec[field?.key ?? field];
      return v == null || v === "" ? "Not stated" : String(v);
    };

    return {
      ready: categories !== null,
      categories: list,
      published: list.filter((c) => c.status === "published").map((c) => c.category),
      names: list.map((c) => c.category),
      forCategory, inputsFor, displayFor, specValue: value_, reload: load,
    };
  }, [categories, load]);

  return <SpecSchemaContext.Provider value={value}>{children}</SpecSchemaContext.Provider>;
}

export const useSpecSchema = () => useContext(SpecSchemaContext);
